import { useEffect, useMemo, useState } from 'react';
import { Point, Measurement, MeasurementKey, MeasurementType, LineMeasurement, isLineMeasurement, UnmeasurableMeasurement, isUnmeasurableMeasurement, DerivedMeasurement, isDerivedMeasurement, MeasurementCollection, MeasurementRequirement, DerivedMeasurementCollection, InputMeasurementCollection, SingleValueMeasurement, joinMeasurementCollections, isNumericMeasurementResult } from '../../../data/Measurements/Measurement';
import { Map } from 'immutable';
import { computeResults, resolve } from '../../../data/Measurements/Resolver';


export function NewMeasurementSystemTestPage() {
    const requiredMeasurements: MeasurementRequirement[] =  [
        { key: 'a-length', type: 'single-value', metadata: { name: 'A Length', description: 'The length of A.', lead: 'A', unit: 'cm' } },
        { key: 'b-length', type: 'single-value', metadata: { name: 'B Length', description: 'The length of B.', lead: 'B', unit: 'cm' } },
        { key: 'a-width', type: 'single-value', metadata: { name: 'A Width', description: 'The width of A.', lead: 'A', unit: 'cm' } },
        { key: 'b-width', type: 'single-value', metadata: { name: 'B Width', description: 'The width of B.', lead: 'B', unit: 'cm' } },
    ]

    const derivedMeasurements: DerivedMeasurementCollection = Map({
        'total-area-divided-by-a': {
            key: 'total-area-divided-by-a',
            type: 'derived',
            dependencies: ['total-area', 'a-area'],
            default: undefined,
            metadata: {
                name: 'Total Area / A',
                description: 'The total area divided by the area of A.',
                lead: 'A',
                unit: 'cm2'
            },
            calculate: (values) => values.get('total-area')! / values.get('a-area')!
        } as DerivedMeasurement,
        'a-area': {
            key: 'a-area',
            type: 'derived',
            dependencies: ['a-length', 'a-width'],
            default: undefined,
             metadata: {
                name: 'Area A',
                description: 'The area of A.',
                lead: 'A',
                unit: 'cm2'
            },
            calculate: (values) => values.get('a-length')! * values.get('a-width')!
        } as DerivedMeasurement,
        'b-area': {
            key: 'b-area',
            type: 'derived',
            dependencies: ['b-length', 'b-width'],
            default: undefined,
            calculate: (values) => values.get('b-length')! * values.get('b-width')!,
            metadata: {
                name: 'Area B',
                description: 'The area of B.',
                lead: 'B',
                unit: 'cm2'
            }
        } as DerivedMeasurement,
        'total-area': {
            key: 'total-area',
            type: 'derived',
            dependencies: ['a-area', 'b-area'],
            default: undefined,
            calculate: (values) => values.get('a-area')! + values.get('b-area')!,
            metadata: {
                name: 'Total Area',
                description: 'The total area of A and B.',
                lead: 'A',
                unit: 'cm2'
            }
        } as DerivedMeasurement,
    })

    /**
     * The collection of input measurements.
     */
    const [inputMeasurements, setInputMeasurements] = useState<InputMeasurementCollection>(Map([]));

    const results = useMemo(() => {
        const allMeasurements = joinMeasurementCollections(derivedMeasurements, inputMeasurements);

        return computeResults(allMeasurements);
    }, [inputMeasurements, derivedMeasurements]);

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <h1 className="text-3xl font-bold mb-6 text-center">Measurement System Test</h1>
    
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    {requiredMeasurements.map((req) => {
                        const value = (inputMeasurements.get(req.key) as SingleValueMeasurement)?.value;
    
                        return (
                            <div key={req.key} className="flex flex-col items-start">
                                <label htmlFor={req.key} className="mb-2 text-lg font-medium text-gray-700">{req.key}</label>
                                
                                <input 
                                  type="number" 
                                  id={req.key} 
                                  value={value ?? ''} 
                                  onChange={(e) => {
                                      const value = parseFloat(e.target.value);
    
                                      setInputMeasurements(inputMeasurements.set(req.key, { key: req.key, type: 'single-value', value, drawable: false }));
                                  }}
                                  className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                  onClick={() => {
                                      setInputMeasurements(inputMeasurements.set(req.key, { key: req.key, type: 'unmeasurable', drawable: false }));
                                  }}
                                  className="p-2 bg-red-500 text-white rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                                >
                                  Unmeasurable
                                </button>
                            </div>
                        );
                    })}
                </div>
    
                <div className="space-y-4">
                    {results.map((result) => (
                        <div key={result.key} className="flex flex-col items-start">
                            <label className="mb-2 text-lg font-medium text-gray-700">{result.key}</label>
                            {isNumericMeasurementResult(result) ? <p className="p-2 border border-gray-300 rounded-md shadow-sm bg-white">{result?.value}</p> : <p className="p-2 border border-gray-300 rounded-md shadow-sm bg-red-100">Unresolvable</p>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}